import type { ShopCurrency } from "@/shared/lib/currency";
import {
  boolean,
  index,
  integer,
  jsonb,
  numeric,
  pgTable,
  text,
  timestamp,
  unique,
  uuid,
  varchar,
} from "drizzle-orm/pg-core";
import { user } from "./auth";

// =====================================================
// Shop / Branch (Merchant) — Single-tenant
// =====================================================

export const shops = pgTable(
  "shops",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    name: varchar("name", { length: 255 }).notNull(),
    status: varchar("status", { length: 50 })
      .$type<"active" | "suspended" | "unverified">()
      .default("unverified")
      .notNull(), // active | suspended | unverified
    verifiedLevel: varchar("verified_level", { length: 50 }).$type<
      "none" | "basic" | "verified"
    >(), // none | basic | verified
    phone: varchar("phone", { length: 50 }),
    address: text("address"),
    city: varchar("city", { length: 100 }),
    /** ເລກປະຈຳຕົວຜູ້ເສຍVATຮ້ານ — snapshot ຕອນອອກໃບກຳກັບ */
    taxId: varchar("tax_id", { length: 64 }),
    /** VAT ເລີ່ມຕົ້ນ POS (%) — ຊຳລະບິນອາດສົ່ງ taxPercent ທັບ */
    defaultTaxPercent: numeric("default_tax_percent", {
      precision: 5,
      scale: 2,
      mode: "number",
    })
      .notNull()
      .default(0),
    /** ສະກຸນເລີ່ມຕົ້ນຂອງຮ້ານ — POS / ສ້າງ SKU (ສາຂາທັບໄດ້ຖ້າມີ branches.default_currency) */
    defaultCurrency: varchar("default_currency", { length: 10 })
      .$type<ShopCurrency>()
      .notNull()
      .default("LAK"),
    /** MC2: ບັງຄັບໃຫ້ shop_product_skus.currency ກົງກັບ default_currency ຂອງຮ້ານ */
    enforceSingleShopSkuCurrency: boolean("enforce_single_shop_sku_currency")
      .notNull()
      .default(false),
    featureFlags: jsonb("feature_flags").notNull().default({}),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [index("shops_status").on(t.status), index("shops_name").on(t.name)],
);

export const branches = pgTable(
  "branches",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    shopId: uuid("shop_id")
      .notNull()
      .references(() => shops.id),
    name: varchar("name", { length: 255 }).notNull(),
    phone: varchar("phone", { length: 50 }),
    address: text("address"),
    isActive: boolean("is_active").notNull().default(true),
    /** ທັບສະກຸນເລີ່ມຕົ້ນຂອງຮ້ານ; null = ໃຊ້ shops.default_currency */
    defaultCurrency: varchar("default_currency", {
      length: 10,
    }).$type<ShopCurrency | null>(),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [index("branches_shop_active").on(t.shopId, t.isActive)],
);

/** Role ต่อร้าน — super admin ของร้านจัดการเอง (ชื่อ role + ชุด permission) */
export const shopRoles = pgTable(
  "shop_roles",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    shopId: uuid("shop_id")
      .notNull()
      .references(() => shops.id, { onDelete: "cascade" }),
    name: varchar("name", { length: 100 }).notNull(),
    /** รายการ permission id (จาก MerchantPermissions) ที่ role นี้มี */
    permissions: text("permissions").array().notNull().default([]),
    sortOrder: integer("sort_order").notNull().default(0),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
    updatedAt: timestamp("updated_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [
    index("shop_roles_shop").on(t.shopId),
    index("shop_roles_shop_name").on(t.shopId, t.name),
  ],
);

export const shopStaff = pgTable(
  "shop_staff",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    shopId: uuid("shop_id")
      .notNull()
      .references(() => shops.id),
    branchId: uuid("branch_id").references(() => branches.id),
    userId: text("user_id")
      .notNull()
      .references(() => user.id),
    /** Role ในร้าน (อิง shop_roles — super admin จัดการได้) */
    shopRoleId: uuid("shop_role_id").references(() => shopRoles.id, {
      onDelete: "restrict",
    }),
    /** เลิกใช้เมื่อใช้ shop_roles แล้ว — เก็บไว้สำหรับ migration/fallback */
    staffRole: varchar("staff_role", { length: 50 }).$type<
      "owner" | "manager" | "cashier"
    >(), // nullable: ใช้ shop_role_id แทน
    isActive: boolean("is_active").notNull().default(true),
    createdAt: timestamp("created_at", {
      withTimezone: true,
    }).notNull(),
  },
  (t) => [
    index("shop_staff_shop_user").on(t.shopId, t.userId),
    index("shop_staff_branch_active").on(t.branchId, t.isActive),
    index("shop_staff_shop_role").on(t.shopRoleId),
    /** ຫນຶ່ງແຖວຕໍ່ (ຮ້ານ, ຜູ້ໃຊ້, ສາຂາ) — branch_id null = ທຸກສາຂາ, ບໍ່ຊ້ຳ null */
    unique("shop_staff_shop_user_branch_uq")
      .on(t.shopId, t.userId, t.branchId)
      .nullsNotDistinct(),
  ],
);
